// M129/M130 Grote Lijnen — CEO Dashboard. Alleen-lezen, herbruikt bestaande
// bronnen via GET /api/dashboard/manager-overview. M130-uitbreidingen:
// - echte winstgevendheidsgrafiek (Recharts) via
//   /api/dashboard/manager-overview/profit-graph
// - Concurrent Radar (top 5 GSC-zoekwoorden) via /api/ceo/competitor-radar
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { TrendingUp, Target, Search, BarChart3, Loader2, AlertTriangle, Radar as RadarIcon, Plus, X } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const EUR = new Intl.NumberFormat("nl-NL", {
  style: "currency", currency: "EUR",
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const NUM = new Intl.NumberFormat("nl-NL");
const fmtEUR = (n) => (n == null ? "—" : EUR.format(Number(n)));
const fmtNum = (n) => (n == null ? "—" : NUM.format(Number(n)));

const _NL_MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec"];
function fmtDayShort(iso) {
  try {
    const d = new Date(iso);
    return `${d.getDate()} ${_NL_MONTHS[d.getMonth()]}`;
  } catch { return iso; }
}

function Tile({ icon: Icon, title, testid, children }) {
  return (
    <div data-testid={testid}
          className="rounded-md border border-border-hairline bg-bg-panel p-4 min-h-[160px] flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-accent-teal" />}
        <div className="text-[10px] text-text-muted font-semibold">
          {title}
        </div>
      </div>
      <div className="flex-1 text-xs text-text-primary">{children}</div>
    </div>
  );
}

function Placeholder({ testid, text }) {
  return (
    <div className="rounded-md border border-border-hairline border-dashed bg-bg-panel/60 p-4 min-h-[120px] flex flex-col justify-center items-center gap-1"
          data-testid={testid}>
      <div className="text-[9px] text-warn-amber font-semibold">
        Voorbeeldweergave
      </div>
      <div className="text-[11px] text-text-muted italic">{text}</div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded border border-border-hairline bg-bg-panel-raised px-2 py-1.5 text-[11px]">
      <div className="text-text-muted mb-1">{fmtDayShort(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="text-text-primary">
          <span style={{ color: p.color }}>■</span> {p.name}: {fmtEUR(p.value)}
        </div>
      ))}
    </div>
  );
}

function ProfitGraph({ testid }) {
  const [pg, setPg] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/manager-overview/profit-graph");
        if (!cancelled) setPg(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Grafiek niet beschikbaar.");
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (err) return (
    <div className="rounded-md border border-border-hairline p-6 text-xs text-text-muted italic" data-testid={testid}>{err}</div>
  );
  if (!pg) return (
    <div className="rounded-md border border-border-hairline p-6 text-xs text-text-muted flex items-center gap-2" data-testid={testid}>
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Grafiek laden…
    </div>
  );
  const days = pg.days || [];
  const ga4Ok = pg.ga4_status === "ok";
  return (
    <div className="rounded-card border border-border-hairline bg-bg-panel-raised p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] text-text-muted font-semibold">
            Winstgevendheid — deze maand
          </div>
          <div className="text-[11px] text-text-subtle">
            Uitgave per dag (Google Ads). {ga4Ok ? "Gemeten opbrengst uit Google Analytics." : "Opbrengst-lijn wacht op activering GA4."}
          </div>
        </div>
      </div>
      {days.length === 0 ? (
        <div className="text-[11px] text-text-subtle italic py-8 text-center">
          Nog geen dagen met uitgave in deze maand.
        </div>
      ) : (
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={days} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid stroke="var(--border-hairline, #1f2937)" strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={fmtDayShort}
                     tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "monospace" }} />
              <YAxis tickFormatter={(v) => `€${v}`}
                     tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "monospace" }} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              <Line type="monotone" dataKey="ads_spend_eur" name="Ads-uitgave"
                    stroke="#22d3ee" strokeWidth={2}
                    dot={{ r: 2 }} isAnimationActive={false} />
              {/* M135-4 (root) — verberg omzet-lijn volledig als GA4-bron niet actief. */}
              {ga4Ok && (
                <Line type="monotone" dataKey="ga4_revenue_eur"
                      name="GA4-opbrengst"
                      stroke="#84cc16" strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls={false} isAnimationActive={false} />
              )}            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-border-hairline text-[11px] text-text-muted flex flex-wrap gap-x-4 gap-y-1">
        <span>Totaal uitgave deze maand: <span className="text-text-primary tabular-nums">{fmtEUR(pg.totals?.ads_spend_eur)}</span></span>
        {pg.totals?.google_plan_eur != null && (
          <span>Plan: <span className="text-text-primary tabular-nums">{fmtEUR(pg.totals.google_plan_eur)}</span></span>
        )}
        {pg.totals?.google_remaining_eur != null && (
          <span>Rest: <span className="text-text-primary tabular-nums">{fmtEUR(pg.totals.google_remaining_eur)}</span></span>
        )}
      </div>
      {pg.ga4_placeholder_note_nl && (
        <div className="mt-2 text-[11px] text-warn-amber italic" data-testid="ceo-profit-graph-ga4-note">
          {pg.ga4_placeholder_note_nl}
        </div>
      )}
    </div>
  );
}

function CompetitorRadar({ testid }) {
  const [cr, setCr] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/ceo/competitor-radar");
      setCr(data); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Concurrent Radar niet beschikbaar.");
    }
  };

  useEffect(() => { load(); }, []);

  const addCompetitor = async () => {
    if (!name.trim()) { setAddErr("Vul een naam in."); return; }
    setAdding(true); setAddErr(null);
    try {
      await api.post("/ceo/competitors", { name: name.trim(), domain: domain.trim() || undefined });
      setName(""); setDomain("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setAddErr(typeof d === "string" ? d : (d?.reason_nl || "Toevoegen mislukt."));
    } finally { setAdding(false); }
  };

  const removeCompetitor = async (id) => {
    try {
      await api.delete(`/ceo/competitors/${id}`);
      await load();
    } catch (e) {
      setAddErr("Verwijderen mislukt.");
    }
  };

  if (err) return <div className="text-xs text-text-muted italic" data-testid={testid}>{err}</div>;
  if (!cr) return (
    <div className="text-xs text-text-muted flex items-center gap-2" data-testid={testid}>
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Concurrent Radar laden…
    </div>
  );

  const manual = cr.manual_competitors || [];
  return (
    <section className="rounded-card border border-border-hairline bg-bg-panel-raised p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <RadarIcon className="w-4 h-4 text-accent-teal" />
        <div className="text-[10px] text-text-muted font-semibold">
          Concurrent Radar (wekelijks)
        </div>
        {cr.period_nl && <span className="text-[10px] text-text-subtle">· {cr.period_nl}</span>}
      </div>

      <div className="mb-4 rounded border border-border-hairline bg-bg-panel p-3" data-testid="ceo-competitor-form">
        <div className="text-[10px] text-text-muted mb-2">
          Voeg een concurrent toe
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="ceo-competitor-name"
                  placeholder="Naam" className="flex-1 text-sm bg-bg-canvas border border-border-hairline rounded px-2 py-1.5 text-text-primary" />
          <input value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="ceo-competitor-domain"
                  placeholder="Website (optioneel)" className="flex-1 text-sm bg-bg-canvas border border-border-hairline rounded px-2 py-1.5 text-text-primary" />
          <button onClick={addCompetitor} disabled={adding} data-testid="ceo-competitor-add"
                   className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal flex items-center gap-1 disabled:opacity-50">
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Toevoegen
          </button>
        </div>
        {addErr && <div className="mt-2 text-[11px] text-warn-amber italic">{addErr}</div>}
      </div>

      {manual.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="ceo-competitor-chips">
          {manual.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-border-hairline bg-bg-panel text-[11px] text-text-primary" data-testid={`ceo-competitor-chip-${c.id}`}>
              {c.name}{c.domain && <span className="text-text-subtle text-[10px]">· {c.domain}</span>}
              <button onClick={() => removeCompetitor(c.id)} className="ml-1 text-text-muted hover:text-warn-amber" data-testid={`ceo-competitor-remove-${c.id}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="mb-4 text-[11px] text-text-subtle italic" data-testid="ceo-competitor-empty">
          {cr.empty_state_nl || "Voeg zelf concurrenten toe die je in de gaten wilt houden."}
        </div>
      )}

      {(cr.keywords || []).length === 0 ? (
        <div className="text-[11px] text-text-subtle italic py-6 text-center">
          Nog geen Search Console-zoekwoorden beschikbaar.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="ceo-competitor-radar-table">
            <thead>
              <tr className="text-left text-text-muted text-[9px]">
                <th className="py-2 pr-2">Zoekwoord</th>
                <th className="py-2 pr-2 text-right">Eigen positie</th>
                <th className="py-2 pr-2 text-right">Klikken</th>
                <th className="py-2 pr-2 text-right">Vertoningen</th>
                {manual.map((c) => (
                  <th key={c.id} className="py-2 pr-2 text-right max-w-[120px] truncate" title={c.name}>{c.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cr.keywords.map((k) => (
                <tr key={k.query} className="border-t border-border-hairline">
                  <td className="py-2 pr-2 text-text-primary truncate max-w-[240px]" title={k.query}>{k.query}</td>
                  <td className="py-2 pr-2 tabular-nums text-right">{k.own_position ?? "—"}</td>
                  <td className="py-2 pr-2 tabular-nums text-right">{fmtNum(k.own_clicks)}</td>
                  <td className="py-2 pr-2 tabular-nums text-right">{fmtNum(k.own_impressions)}</td>
                  {manual.map((c) => (
                    <td key={c.id} className="py-2 pr-2 text-right text-text-subtle italic text-[10px]">
                      {cr.cell_note_nl || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function CeoDashboardPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [pending, setPending] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/manager-overview");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Overzicht niet beschikbaar.");
      }
      try {
        const { data } = await api.get("/site-audit/pending-count");
        if (!cancelled) setPending(data);
      } catch {
        /* niet blokkerend */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (err) return <AppLayout><div className="text-text-muted text-sm" data-testid="ceo-page-error">{err}</div></AppLayout>;
  if (!data) return <AppLayout><div className="text-text-muted text-sm flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Ondernemersdashboard laden…</div></AppLayout>;
  const t = data.tiles || {};
  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-display text-text-primary tracking-tight" data-testid="ceo-dashboard-title">Ondernemersdashboard</h1>
        <p className="text-xs text-text-muted mt-1">MANAGER-OVERZICHT · ALLEEN-LEZEN · ADVIESMODUS</p>
      </div>

      {pending && pending.total_waiting > 0 && (
        <a href="/publish-queue" data-testid="ceo-approval-strip"
            className="mb-4 flex items-center justify-between gap-3 rounded-card border border-accent-teal/40 bg-accent-teal/5 px-4 py-3 hover:bg-accent-teal/10 transition-colors">
          <div>
            <div className="text-[10px] text-accent-teal font-semibold">Wachtend op jouw goedkeuring</div>
            <div className="text-sm text-text-primary mt-0.5">
              <span className="tabular-nums" data-testid="ceo-approval-strip-count">{pending.total_waiting}</span> item(s) klaar in Publish Queue —
              {" "}<span className="text-text-muted">{pending.queue_draft} concept</span>
              {pending.queue_approved > 0 && <>, <span className="text-text-muted">{pending.queue_approved} goedgekeurd</span></>}
              {pending.seo_ready_to_queue > 0 && <>, <span className="text-text-muted">{pending.seo_ready_to_queue} SEO-oplossing klaar</span></>}
            </div>
          </div>
          <span className="text-[11px] text-accent-teal">Openen →</span>
        </a>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="ceo-tiles">
        <Tile icon={TrendingUp} title="Uitgave deze maand (Google Ads)" testid="ceo-tile-spend">
          {t.spend_this_month?.available ? (
            <>
              <div className="text-[10px] text-text-subtle mb-1">{t.spend_this_month.period_nl}</div>
              <div className="text-xl text-text-primary tabular-nums">{fmtEUR(t.spend_this_month.spent_eur)}</div>
              <div className="text-[11px] text-text-muted mt-1">
                Plan {fmtEUR(t.spend_this_month.plan_eur)} · Rest {fmtEUR(t.spend_this_month.remaining_eur)}
              </div>
              {t.spend_this_month.projected_end_of_month_eur != null && (
                <div className="mt-2 pt-2 border-t border-border-hairline">
                  <div className="text-[10px] text-text-muted">Prognose einde maand</div>
                  <div className="text-sm text-text-primary tabular-nums">{fmtEUR(t.spend_this_month.projected_end_of_month_eur)}</div>
                  {t.spend_this_month.possible_overshoot_eur != null && (
                    <div className="text-[11px] text-warn-amber">Mogelijke overschrijding: {fmtEUR(t.spend_this_month.possible_overshoot_eur)}</div>
                  )}
                </div>
              )}
            </>
          ) : <div className="text-[11px] text-text-subtle italic mt-2">Nog geen gegevens deze maand.</div>}
        </Tile>

        <Tile icon={Target} title="Gemeten resultaat (Ads)" testid="ceo-tile-results">
          {t.measured_results?.available ? (
            <>
              <div className="text-[10px] text-text-subtle mb-1">{t.measured_results.period_nl}</div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div><div className="text-text-muted">Klikken</div><div className="text-sm tabular-nums">{fmtNum(t.measured_results.clicks)}</div></div>
                <div><div className="text-text-muted">Vertoningen</div><div className="text-sm tabular-nums">{fmtNum(t.measured_results.impressions)}</div></div>
                <div><div className="text-text-muted">Conversies</div><div className="text-sm tabular-nums">{fmtNum(t.measured_results.conversions)}</div></div>
              </div>
              {t.measured_results.cost_per_conversion_eur != null ? (
                <div className="mt-2 text-[11px] text-text-muted">Kosten per conversie: <span className="text-text-primary tabular-nums">{fmtEUR(t.measured_results.cost_per_conversion_eur)}</span></div>
              ) : t.measured_results.note_nl && (
                <div className="mt-2 text-[11px] text-text-muted italic">{t.measured_results.note_nl}</div>
              )}
            </>
          ) : <div className="text-[11px] text-text-subtle italic mt-2">Nog geen gegevens deze maand.</div>}
        </Tile>

        <Tile icon={Search} title="SEO-zichtbaarheid (Search Console)" testid="ceo-tile-seo">
          {t.seo_visibility?.available ? (
            <>
              <div className="text-[10px] text-text-subtle mb-1">{t.seo_visibility.period_nl}</div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div><div className="text-text-muted">Klikken</div><div className="text-sm tabular-nums">{fmtNum(t.seo_visibility.clicks)}</div></div>
                <div><div className="text-text-muted">Vertoningen</div><div className="text-sm tabular-nums">{fmtNum(t.seo_visibility.impressions)}</div></div>
                <div><div className="text-text-muted">CTR</div><div className="text-sm tabular-nums">{t.seo_visibility.ctr_pct}%</div></div>
                <div><div className="text-text-muted">Gem. positie</div><div className="text-sm tabular-nums">{t.seo_visibility.avg_position}</div></div>
              </div>
              {t.seo_visibility.last_date_with_data_nl && (
                <div className="mt-2 text-[10px] text-text-subtle">Laatste datum: {t.seo_visibility.last_date_with_data_nl}</div>
              )}
            </>
          ) : <div className="text-[11px] text-text-subtle italic mt-2">Nog geen Search Console-gegevens.</div>}
        </Tile>

        <Tile icon={BarChart3} title={t.customer_conversion?.header_nl || "Klant- en conversie-inzage (GA4)"} testid="ceo-tile-ga4">
          {/* M135-4 (root) — bron-badge boven-in: duidelijk of GA4 actief
              is als bron ("Bron: GA4") of niet ("Bron: —"). */}
          <div className="mb-2 flex items-center gap-1">
            <span className={`text-[9px] px-1.5 py-0.5 rounded border ${ t.customer_conversion?.status === "ok" ? "text-pos-green border-pos-green/40 bg-pos-green/10" : "text-text-muted border-border-hairline bg-bg-panel-raised"}`}
                data-testid="ceo-tile-ga4-source-badge">
              {t.customer_conversion?.status === "ok" ? "Bron: GA4" : "Bron: —"}
            </span>
          </div>
          {t.customer_conversion?.status === "api_disabled" ? (
            <div className="text-[11px] text-warn-amber leading-relaxed" data-testid="ceo-tile-ga4-api-disabled">
              <AlertTriangle className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
              JustEasySEO moet deze dienst nog activeren. Zolang activering ontbreekt, zijn er geen Google Analytics-gegevens beschikbaar voor analyse.
            </div>
          ) : t.customer_conversion?.available && t.customer_conversion?.status === "ok" ? (
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div><div className="text-text-muted">Sessies</div><div className="text-sm tabular-nums">{fmtNum(t.customer_conversion.sessions)}</div></div>
              <div><div className="text-text-muted">Gebruikers</div><div className="text-sm tabular-nums">{fmtNum(t.customer_conversion.users)}</div></div>
              <div><div className="text-text-muted">Conversies</div><div className="text-sm tabular-nums">{fmtNum(t.customer_conversion.conversions)}</div></div>
              {t.customer_conversion.revenue_eur != null && <div><div className="text-text-muted">Omzet</div><div className="text-sm tabular-nums">{fmtEUR(t.customer_conversion.revenue_eur)}</div></div>}
            </div>
          ) : (
            /* M135-4 — bronwaarheid: geen fake 0-cijfers. Toon "—" i.p.v. 0 en de eerlijke wacht-tekst. */
            <div data-testid="ceo-tile-ga4-waiting">
              <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
                <div><div className="text-text-muted">Sessies</div><div className="text-sm tabular-nums text-text-muted">—</div></div>
                <div><div className="text-text-muted">Gebruikers</div><div className="text-sm tabular-nums text-text-muted">—</div></div>
                <div><div className="text-text-muted">Conversies</div><div className="text-sm tabular-nums text-text-muted">—</div></div>
                <div><div className="text-text-muted">Omzet</div><div className="text-sm tabular-nums text-text-muted">—</div></div>
              </div>
              <div className="text-[11px] text-warn-amber/90 italic leading-relaxed">
                {t.customer_conversion?.message_nl || "Google Analytics is nog niet gekoppeld."}
              </div>
            </div>
          )}
        </Tile>
      </section>

      <section className="rounded-card border border-border-hairline bg-bg-panel-raised p-5 mb-6">
        <div className="text-[10px] text-text-muted font-semibold mb-2">Winstgevendheid deze maand</div>
        <div className="text-sm text-text-primary leading-relaxed" data-testid="ceo-profit-summary">{data.profitability_summary_nl}</div>
      </section>

      <section className="mb-6" data-testid="ceo-profit-graph-section">
        <ProfitGraph testid="ceo-profit-graph" />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="ceo-placeholders">
        <Placeholder testid="ceo-placeholder-trend" text="Trendlijn per week volgt zodra 4+ weken data beschikbaar zijn." />
        <Placeholder testid="ceo-placeholder-benchmarks" text="Branche-benchmarks volgen zodra externe bron gekoppeld is." />
      </section>

      <CompetitorRadar testid="ceo-competitor-radar" />
    </AppLayout>
  );
}
